import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Sparkles, CheckCircle2, Building2, AlertTriangle, TrendingDown, Eye, Users, Clock } from "lucide-react";
import { ClientLogos } from "@/components/ClientLogos";
import { ScheduleDemo } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { PageResources } from "@/components/sections/PageResources";

import { PageContainer } from "@/components/layouts/MainLayout";
import { T, THtml } from "@/components/editable/T";
import { EditableImage } from "@/components/editable/EditableImage";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import workDesignHero from "@/assets/images/work-design-hero-photo.webp";
import jobProfileImage from "@/assets/images/functieprofiel-hr-manager.webp";
import jobProfileImageEn from "@/assets/images/functieprofiel-hr-manager-en.webp";
import jobProfileImageDe from "@/assets/images/functieprofiel-hr-manager-de.webp";
import functiematrixNl from "@/assets/images/functiematrix-nl.webp";
import functiematrixEn from "@/assets/images/functiematrix-en.webp";
import functiematrixDe from "@/assets/images/functiematrix-de.webp";
import salarisschalenNl from "@/assets/images/salarisschalen-nl.webp";
import salarisschalenEn from "@/assets/images/salarisschalen-en.webp";
import salarisschalenDe from "@/assets/images/salarisschalen-de.webp";


const P = "workDesign";

const featureColors = [
  "#F3EDFD", "#FDF2ED", "#FFFBEB", "#FAEDFD"];


const WorkDesign = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'nl';
  const functiematrixImage = lang === 'de' ? functiematrixDe : lang === 'en' ? functiematrixEn : functiematrixNl;
  const functiematrixAlt = lang === 'de'
    ? 'Funktionsmatrix mit Funktionsfamilien, Stufen und Gehaltsbandbreiten in Learned'
    : lang === 'en'
    ? 'Job matrix with function families, levels and salary ranges in Learned'
    : 'Functiematrix met functiefamilies, niveaus en salarisbandbreedtes in Learned';

  const features = [
  {
    eyebrowKey: "features.row1.eyebrow",
    eyebrowDefault: "Functiewaardering",
    titleKey: "features.row1.title",
    titleDefault: "Maak inschaling verdedigbaar met de methodiek die bij jou past",
    bodyKey: "features.row1.body",
    bodyDefault: "Kies via ons netwerk van erkende partners de functiewaarderingsmethodiek die het beste aansluit bij jouw organisatie, en weeg elke rol objectief op basis van diezelfde criteria.",
    benefits: [
    { k: "features.row1.benefit1", d: "Methode-onafhankelijk, kies wat bij je past" },
    { k: "features.row1.benefit2", d: "Objectieve weging, dezelfde criteria voor iedereen" },
    { k: "features.row1.benefit3", d: "Geen scheefgegroeid functiehuis meer" }],

    imageKey: 'row1-image',
    imgSrc: functiematrixImage,
    imgAlt: functiematrixAlt,
    color: featureColors[0],
    cta: {
      labelKey: "features.row1.cta",
      labelDefault: "Ontdek functiehuis en functieprofielen",
      link: getLocalizedRoutePath("jobProfiles", lang)
    }
  },
  {
    eyebrowKey: "jobMatrix.eyebrow",
    eyebrowDefault: "Job Matrix (functiehuis)",
    titleKey: "jobMatrix.title",
    titleDefault: "Een compleet, actueel functiehuis in korte tijd",
    bodyKey: "jobMatrix.body",
    bodyDefault: "Bouw in korte tijd een overzichtelijk functiehuis met AI-gegenereerde functieprofielen, gebaseerd op competenties en skills, uitgedrukt in concreet gedrag in plaats van losse taken. Zo maak je meteen concreet wat succesvol zijn betekent binnen jouw organisatie. Direct bruikbaar en eenvoudig aan te passen.",
    benefits: [
    { k: "jobMatrix.benefit1", d: "Genereer ieder profiel met AI" },
    { k: "jobMatrix.benefit2", d: "Één overzichtelijk functiehuis met rollen en niveaus" },
    { k: "jobMatrix.benefit3", d: "Eenvoudig te actualiseren, geen verouderde documenten meer" }],

    imageKey: 'job-matrix',
    imgSrc: lang === 'en' ? jobProfileImageEn : lang === 'de' ? jobProfileImageDe : jobProfileImage,
    imgAlt: lang === 'en' ? 'HR Manager job profile with competencies, skills and behavioural examples in Learned' : lang === 'de' ? 'HR-Manager Funktionsprofil mit Kompetenzen, Fähigkeiten und Verhaltensbeispielen in Learned' : 'Functieprofiel HR Manager met competenties, skills en gedragsvoorbeelden in Learned',
    color: featureColors[0],
    cta: {
      labelKey: "jobMatrix.cta",
      labelDefault: "Ontdek functiehuis en functieprofielen",
      link: getLocalizedRoutePath("jobProfiles", lang)
    }
  },
  {
    eyebrowKey: "features.row2.eyebrow",
    eyebrowDefault: "Salarisschalen & competenties",
    titleKey: "features.row2.title",
    titleDefault: "Duidelijke salarisschalen, gekoppeld aan concreet gedrag",
    bodyKey: "features.row2.body",
    bodyDefault: "Koppel elk niveau aan concreet, waarneembaar gedrag, van beginner tot expert, en aan salarisschalen gebaseerd op actuele marktbenchmarks.",
    benefits: [
    { k: "features.row2.benefit1", d: "Salarisschalen per niveau, gebaseerd op actuele benchmarks" },
    { k: "features.row2.benefit2", d: "Gedragsgerichte competenties per functie en niveau" },
    { k: "features.row2.benefit3", d: "Directe basis voor objectieve evaluatie en eerlijke beloning" }],

    imageKey: 'row2-image',
    imgSrc: lang === 'en' ? salarisschalenEn : lang === 'de' ? salarisschalenDe : salarisschalenNl,
    imgAlt: lang === 'en' ? 'Pay ranges per job level with employee positions' : lang === 'de' ? 'Gehaltsbereiche je Jobebene mit Mitarbeiterpositionen' : 'Salarisschalen per functieniveau met posities van medewerkers',
    color: featureColors[1],
    badge: { k: "features.row2.badge", d: "Conform wet Loontransparantie" },
    demoCta: true
  }];






  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <p className="text-lead text-muted-foreground mb-4">
                <T page={P} k="hero.subtitle" d="Learned Work Design" />
              </p>
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={P} k="hero.titleLead" d="Leg de basis vast" as="span" className="heading-lead" />
                <T page={P} k="hero.titleAccent" d="waar alles op rust" as="span" className="heading-accent" />
              </h1>
              <THtml page={P} k="hero.body" d="Van rol tot niveau tot salarisschaal: leg het fundament met functieprofielen op basis van skills, gedrag en KPI's. Een gewogen structuur die voor iedereen hetzelfde is, verwachtingen transparant maakt en de basis legt voor objectief en transparant evalueren, ontwikkelen en eerlijk belonen." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />

              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={P} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={P} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>

            <div className="animate-fade-in relative" style={{ animationDelay: '200ms' }}>
              <EditableImage
                page={P}
                section="hero"
                imageKey="hero-image"
                src={workDesignHero}
                alt="Twee collega's werken samen aan een functiehuis met post-its op een wand"
                className="w-full h-[450px] object-cover object-center rounded-2xl"
                loading="eager"
              />
              <div className="absolute -right-8 -top-5 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <Sparkles className="w-4 h-4 text-[#9055EE]" />
                <T page={P} k="hero.cards.profiles" d="Genereer ieder profiel met AI" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -right-6 bottom-24 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <T page={P} k="hero.cards.methodIndependent" d="Methode-onafhankelijk" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -left-6 bottom-10 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1700ms', animationFillMode: 'forwards' }}>
                <Building2 className="w-4 h-4 text-[#FB9A24]" />
                <T page={P} k="hero.cards.weighted" d="Gewogen rollen en niveaus" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
            </div>

          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      {/* Intro banner */}
      <section className="px-6 py-20" style={{ backgroundColor: '#F3EDFD' }}>
        <PageContainer>
          <div className="text-center">
            <T page={P} k="intro.eyebrow" d="Learned Work Design" as="p" className="text-xs font-semibold uppercase tracking-wider text-[#9055EE] mb-2" />
            <h2 className="feature-section-heading text-foreground mb-4">
              <T page={P} k="intro.title" d="Je zaken op orde: nooit meer een verouderd functiehuis" />
            </h2>
            <THtml
              page={P}
              k="intro.body"
              d="Learned Work Design helpt je een gewogen functiehuis met gebenchmarkte salarisschalen op te bouwen, met de methodiek die bij jouw organisatie past. Zo weet je precies wat elke functie vraagt, welk niveau daarbij hoort, en welke salarisschaal daarbij aansluit. Vanuit deze basis evalueer en ontwikkel je met Performance Management en beloon je met Compensation: op basis van heldere verwachtingen, en eerlijke, transparante beloning."
              as="p"
              className="text-lead text-muted-foreground max-w-4xl mx-auto"
            />
            <div className="mt-8">
              <Button
                size="lg"
                onClick={() => document.getElementById('work-design-onderdelen')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <T page={P} k="intro.ctaParts" d="Ontdek de onderdelen" />
              </Button>
            </div>
          </div>
        </PageContainer>
      </section>
      {/* Challenge Block */}
      <section
        className="overflow-x-hidden px-4 py-20 sm:px-6"
        style={{ background: "linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)" }}
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
              {[
                { icon: AlertTriangle, k: "challenges.pain1", d: "Functies zijn niet gewogen" },
                { icon: TrendingDown, k: "challenges.pain2", d: "Verouderde salarisschalen" },
                { icon: Eye, k: "challenges.pain3", d: "Geen inzicht in personeelsplanning" },
                { icon: Users, k: "challenges.pain4", d: "Onduidelijke doorgroeimogelijkheden" },
                { icon: Clock, k: "challenges.pain5", d: "Te hoge loonkosten & tijdrovende processen" },
              ].map((point, i) => {
                const Icon = point.icon;
                return (
                  <div
                    key={i}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-white/10 px-4 py-3 backdrop-blur-sm sm:w-auto sm:px-5"
                  >
                    <Icon className="w-4 h-4 text-white/60 shrink-0" />
                    <T page={P} k={point.k} d={point.d} as="span" className="text-center text-sm leading-snug text-white/80" />
                  </div>
                );
              })}
            </div>

            <div>
              <T page={P} k="challenges.eyebrow" d="De uitdaging" as="p" className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-2" />
              <h2 className="feature-section-heading text-white mb-6">
                <T page={P} k="challenges.title" d="Zonder gewogen structuur, geen duidelijkheid" />
              </h2>
              <THtml
                page={P}
                k="challenges.body"
                d="Zou jij kunnen uitleggen waarom de ene functie zwaarder weegt dan de andere? Veel organisaties niet: functies zijn nooit gewogen, profielen zijn verouderd, en nieuwe rollen komen er ad hoc bij. Het gevolg: onduidelijke doorgroei en discussies over inschaling, met gevolgen tot in je performance management en beloningsbeslissingen. Want hoe weet je nu of iemand succesvol in zijn functie is, en welke beloning hierbij hoort?"
                as="p"
                className="text-sm text-white/80 leading-relaxed"
              />
            </div>
          </div>
        </div>
      </section>







      {/* Features Section */}


      <section id="work-design-onderdelen" className="pt-[60px] pb-10 px-6 scroll-mt-24">

        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <T page={P} k={feature.eyebrowKey} d={feature.eyebrowDefault} as="p" className="text-xs font-semibold uppercase tracking-wider text-[#9055EE] mb-2" />
                    <h2 className="feature-section-heading text-foreground">
                      <T page={P} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={P} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    <ul className="mt-6 space-y-3">
                      {feature.benefits.map((benefit) =>
                      <li key={benefit.k} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-[#9055EE] shrink-0 mt-0.5" />
                          <T page={P} k={benefit.k} d={benefit.d} as="span" className="text-sm text-muted-foreground" />
                        </li>
                      )}
                    </ul>



                    {feature.cta &&
                    <Button
                      className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white"
                      onClick={() => navigate(feature.cta!.link)}>

                        <T page={P} k={feature.cta.labelKey} d={feature.cta.labelDefault} />
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    }
                    {feature.demoCta &&
                    <Button
                      className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white"
                      onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>

                        <T page={P} k="features.demoCta" d="Plan een demo" />
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    }
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'} relative`}>
                    {('imgSrc' in feature ? (feature as { imgSrc?: string }).imgSrc : undefined) ?
                    <EditableImage
                      page={P}
                      section="features"
                      imageKey={feature.imageKey}
                      src={(feature as { imgSrc?: string }).imgSrc || ''}
                      alt={('imgAlt' in feature ? (feature as { imgAlt?: string }).imgAlt : undefined) || ''}
                      loading="lazy"
                      className="w-full h-auto rounded-2xl"
                    /> :
                    <div
                      className="rounded-2xl p-4 md:p-6 flex items-center justify-center relative overflow-hidden min-h-[400px] max-h-[450px]"
                      style={{
                        background: `radial-gradient(circle at center, white 0%, ${feature.color} 70%)`,
                        border: '10px solid rgba(255, 255, 255, 0.7)'
                      }}>

                      {/* TODO: beeld toevoegen via de CMS (Visuals: page workDesign, section features) */}
                      <EditableImage
                        page={P}
                        section="features"
                        imageKey={feature.imageKey}
                        src=""
                        alt=""
                        loading="lazy"
                        className="w-full max-w-full max-h-[400px] object-contain relative z-10 mx-auto"
                      />
                    </div>}
                    {('badge' in feature ? (feature as { badge?: { k: string; d: string } }).badge : undefined) &&
                    <div className="absolute -right-4 top-8 bg-[#4B2A8A] rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 hidden lg:flex items-center gap-2 z-20">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <T page={P} k={(feature as { badge: { k: string; d: string } }).badge.k} d={(feature as { badge: { k: string; d: string } }).badge.d} as="span" className="text-sm font-semibold text-white" />
                    </div>}
                  </div>
                </div>);

            })}
          </div>
        </PageContainer>
      </section>


      <section className="px-6 pt-0 pb-12">
        <PageContainer>
          <div
            className="rounded-2xl px-8 py-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
            style={{ backgroundColor: '#F3EDFD' }}>

            <p className="text-base text-foreground">
              <T page={P} k="nextModule.text" d="Staat het fundament? Nu kun je objectief evalueren en gericht ontwikkelen met Performance Management." />
            </p>
            <Link
              to={getLocalizedRoutePath("performanceManagement", lang)}
              className="inline-flex items-center gap-2 shrink-0 text-base font-medium text-[#9055EE] hover:text-[#753DCE] transition-colors">

              <T page={P} k="nextModule.link" d="Ontdek Performance Management" />
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </PageContainer>
      </section>




      {lang === 'nl' && (
        <PageResources page="work-design" cards={[
          { href: "/nl/blog/opstellen-van-functiehuis/", image: "/images/blog/opstellen-van-functiehuis-hero.webp", alt: "Functiehuis opstellen", type: "blog", titleKey: "resources.card1.title", titleDefault: "Functiehuis opstellen: stappenplan en voorbeelden" },
          { href: "/nl/eguide/functieprofiel-opstellen/", image: "/images/eguide-functieprofiel-hero.png", alt: "E-guide functieprofiel opstellen", type: "eguide", titleKey: "resources.card2.title", titleDefault: "E-guide: skills-based functieprofiel opstellen (met of zonder AI)" },
          { href: "/nl/webinar/functiehuis-performance-beloning/", image: "/images/resources/functiehuis-performance-beloning.png", alt: "Webinar functiehuis, performance en beloning", type: "webinar", titleKey: "resources.card3.title", titleDefault: "On-demand webinar: verbind functiehuis, performance en beloning in één HR-aanpak" },
        ]} />
      )}

      <PartnerForChange />

      <ScheduleDemo titleKey="titleWorkDesign" titleDefault="Klaar om het fundament te leggen?" />
    </>);

};

export default WorkDesign;
